
import React from "react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend, Cell
} from "recharts";

interface DeviceStatusDistributionChartProps {
  counts: {
    active: number;
    inactive: number;
    pending: number;
    total: number;
  };
}

export function DeviceStatusDistributionChart({ counts }: DeviceStatusDistributionChartProps) {
  // Debug logging
  console.log('DeviceStatusDistributionChart rendering with counts:', counts);
  
  // Status distribution data using real device counts
  const data = [
    { name: 'Active', value: counts?.active || 0, color: '#6366F1' },    // Primary Purple/Blue
    { name: 'Inactive', value: counts?.inactive || 0, color: '#FF8C66' },   // Orange/Coral
    { name: 'Pending', value: counts?.pending || 0, color: '#4ADE80' },    // Green
  ];
  
  // Always show data, even if zero
  const filteredData = data.filter(item => item.value > 0);
  const chartData = filteredData.length > 0 ? filteredData : [
    { name: 'No Data', value: 1, color: '#E5E7EB' }
  ];
  
  console.log('Chart data being rendered:', chartData);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const total = data.reduce((sum, item) => sum + item.value, 0);
      return (
        <div className="bg-white p-3 shadow-md rounded-md border border-gray-100">
          <p className="font-medium text-sm">{`${payload[0].name}`}</p>
          <p className="text-sm">{`Devices: ${payload[0].value}`}</p>
          <p className="text-xs text-gray-500">{`${Math.round((payload[0].value / total) * 100)}% of total`}</p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis 
            type="number" 
            tick={{ fontFamily: 'Poppins, sans-serif', fontSize: 11 }}
          />
          <YAxis 
            dataKey="name" 
            type="category" 
            scale="band" 
            tick={{ fontFamily: 'Poppins, sans-serif', fontSize: 11 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            iconType="circle"
            iconSize={10}
            wrapperStyle={{ fontFamily: 'Poppins, sans-serif', fontSize: 12 }}
          />
          <Bar 
            dataKey="value" 
            name="Devices"
            radius={[0, 4, 4, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
